import { AttachMoney, Download, Percent, PlayArrow } from '@mui/icons-material';
import { Button, ButtonGroup, ToggleButton, ToggleButtonGroup } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { CommodityManagementApi } from '../../../../../../Apis/Apis';
import { Constants } from '../../../../../../Core/Constants';
import { CustomerReportModel, StressIncrementType } from '../../../../../../Generated/Commodity-Management-Api';
import { downloadFile } from '../../../../../../Helpers';
import { deepMerge } from '../../../../../../Helpers/ObjectHelper';
import { useLoadingState } from '../../../../../../Hooks';
import { RootState, useApplicationSelector } from '../../../../../../Redux/ReduxStore';
import { StoneXLoading, StoneXRow } from '../../../../../StoneX';
import { StoneXButton, StoneXDatePicker, StoneXTextField } from '../../../../../StoneXMui';
import { ReportProps } from '../../ReportsPage';
import ExposureReportSettingsModal from './ExposureReportSettingsModal';
import ExposureReportTable from './ExposureReportTable';
import { ExposureReportConfiguration, ExposureReportRow, ExposureReportUserOverride } from './Types';

function flattenCounterparties(counterparties: any[]): ExposureReportRow[] {
    const rows: ExposureReportRow[] = [];

    const sortedCounterparties = [...(counterparties ?? [])].sort((a, b) =>
        (a.counterpartyName ?? '').localeCompare(b.counterpartyName ?? ''));

    for (const counterparty of sortedCounterparties) {
        const counterpartyKey = `cp:${counterparty.counterpartyId ?? counterparty.counterpartyName ?? ''}`;
        rows.push({
            path: [counterpartyKey],
            rowType: 'Counterparty',
            counterpartyId: counterparty.counterpartyId,
            counterpartyName: counterparty.counterpartyName,
            counterpartyCreditLimit: counterparty.counterpartyCreditLimit,
            availableCredit: counterparty.availableCredit,
            counterpartyNetting: counterparty.counterpartyNetting,
            counterpartySetOff: counterparty.counterpartySetOff,
            markToMarketValue: counterparty.markToMarketValue,
            stressValues: counterparty.stressValues ?? {},
            availableCredits: counterparty.availableCredits ?? {},
        });

        const sortedCommodities = [...(counterparty.commodities ?? [])].sort((a, b) =>
            (a.commodity?.commodityName ?? '').localeCompare(b.commodity?.commodityName ?? ''));

        for (const commodityNode of sortedCommodities) {
            const commodity = commodityNode.commodity;
            const commodityKey = `${counterpartyKey}|cm:${commodity?.commodityId ?? commodity?.commodityName ?? ''}`;
            rows.push({
                path: [counterpartyKey, commodityKey],
                rowType: 'Commodity',
                commodity,
                markToMarketValue: commodityNode.markToMarketValue,
                stressValues: commodityNode.stressValues ?? {},
            });

            const sortedDealTickets = [...(commodityNode.dealTickets ?? [])].sort((a, b) =>
                (a.dealTicket ?? '').localeCompare(b.dealTicket ?? ''));

            for (const dealTicket of sortedDealTickets) {
                const dealTicketKey = `${commodityKey}|dt:${dealTicket.tradeId ?? dealTicket.dealTicket ?? ''}`;
                rows.push({
                    path: [counterpartyKey, commodityKey, dealTicketKey],
                    rowType: 'DealTicket',
                    tradeId: dealTicket.tradeId,
                    dealTicket: dealTicket.dealTicket,
                    commodity,
                    volume: dealTicket.volume,
                    markToMarketValue: dealTicket.markToMarketValue,
                    stressValues: dealTicket.stressValues ?? {},
                });

                for (const position of dealTicket.positions ?? []) {
                    rows.push({
                        path: [counterpartyKey, commodityKey, dealTicketKey, `p:${position.positionId}`],
                        rowType: 'Position',
                        positionId: position.positionId,
                        portfolioId: position.portfolioId,
                        portfolioName: position.portfolioName,
                        commodity,
                        side: position.side,
                        contractMonth: position.contractMonth,
                        volume: position.volume,
                        price: position.price,
                        marketPrice: position.marketPrice,
                        markToMarketPrice: position.markToMarketPrice,
                        markToMarketValue: position.markToMarketValue,
                        stressPrices: position.stressPrices ?? {},
                        stressValues: position.stressValues ?? {},
                    });
                }
            }
        }
    }

    return rows;
}

export function ExposureReport(props: ReportProps) {
    const { customerName, customerReportId } = props;

    const customerReport = useApplicationSelector((state: RootState): CustomerReportModel =>
        state.commodityManagement.customerReports.find((x: any) => x.customerReportId === customerReportId)!);

    const [quoteDate, setQuoteDate] = useState<Date | null>(props.quoteDate);
    const quoteDateString = useMemo(() => quoteDate ? dayjs(quoteDate).format('YYYY-MM-DD') : null, [quoteDate]);

    const reportLoadingState = useLoadingState();
    const reportDownloadingState = useLoadingState();
    const reportIsBusy = reportLoadingState.isLoading() || reportDownloadingState.isLoading();

    const [rows, setRows] = useState<ExposureReportRow[]>([]);

    const reportConfiguration = useMemo(
        () => JSON.parse(customerReport.configuration!) as ExposureReportConfiguration,
        [customerReport],
    );
    const userOverrides = useMemo(
        () => customerReport.userOverrides ? JSON.parse(customerReport.userOverrides) as ExposureReportUserOverride : undefined,
        [customerReport],
    );
    const reportSettings = useMemo(() => deepMerge(reportConfiguration, userOverrides), [reportConfiguration, userOverrides]);

    const [stressIncrementsText, setStressIncrementsText] = useState<string>((reportSettings?.behavior?.stressIncrements ?? [5, 10, 15]).join(', '));
    const [stressIncrementType, setStressIncrementType] = useState<StressIncrementType>(reportSettings?.behavior?.stressIncrementType ?? StressIncrementType.Percent);

    const [isConfiguringReport, setIsConfiguringReport] = useState<boolean>(false);

    const stressOptions = useMemo<number[]>(() => {
        const keys = new Set<number>();
        rows.forEach((row) => {
            Object.keys(row.stressValues ?? {}).forEach((key) => keys.add(Number(key)));
            Object.keys(row.stressPrices ?? {}).forEach((key) => keys.add(Number(key)));
        });
        return Array.from(keys).filter((value) => !Number.isNaN(value)).sort((a, b) => a - b);
    }, [rows]);

    const [activeStressIncrement, setActiveStressIncrement] = useState<number | null>(0);

    useEffect(() => {
        if (activeStressIncrement === null || !stressOptions.includes(activeStressIncrement)) {
            setActiveStressIncrement(stressOptions.includes(0) ? 0 : (stressOptions[0] ?? null));
        }
    }, [stressOptions, activeStressIncrement]);

    useEffect(() => {
        setStressIncrementsText((reportSettings?.behavior?.stressIncrements ?? [5, 10, 15]).join(', '));
        setStressIncrementType(reportSettings?.behavior?.stressIncrementType ?? StressIncrementType.Percent);
    }, [reportSettings]);

    function runReport() {
        if (!quoteDateString) {
            return;
        }

        const stressIncrements = stressIncrementsText
            .split(',')
            .map((value) => Number(value.trim()))
            .filter((value) => !Number.isNaN(value));

        CommodityManagementApi.getExposureReport(
            reportLoadingState.setLoadingState,
            quoteDateString,
            customerReportId,
            undefined,
            stressIncrementType,
            stressIncrements,
        ).then((response) => {
            const counterparties = (response.data as any).rows ?? [];
            setRows(flattenCounterparties(counterparties));
        });
    }

    function downloadToExcel() {
        if (!quoteDateString) {
            return;
        }

        const stressIncrements = stressIncrementsText
            .split(',')
            .map((value) => Number(value.trim()))
            .filter((value) => !Number.isNaN(value));

        CommodityManagementApi.getExposureExcelReport(
            reportDownloadingState.setLoadingState,
            quoteDateString,
            customerReportId,
            undefined,
            stressIncrementType,
            stressIncrements,
            undefined,
            undefined,
            undefined,
            undefined,
            { responseType: 'blob', headers: { 'ContentType': Constants.MediaType.Excel } },
        ).then(downloadFile);
    }

    function onStressIncrementsChange(event: any) {
        setStressIncrementsText(event?.target?.value);
    }

    function onStressIncrementTypeChange(_event: React.MouseEvent<HTMLElement>, newValue: string | null) {
        if (newValue !== null) {
            setStressIncrementType(newValue as StressIncrementType);
        }
    }

    function onActiveStressIncrementChange(_event: React.MouseEvent<HTMLElement>, newValue: number | null) {
        if (newValue !== null) {
            setActiveStressIncrement(newValue);
        }
    }

    function formatStressLabel(increment: number) {
        const sign = increment > 0 ? '+' : '';
        return stressIncrementType === StressIncrementType.Percent 
            ? `${sign}${increment}%`
            : `${sign}\$${increment}`;
    }

    function closeModal() {
        setIsConfiguringReport(false);
    }

    return (
        <div>
            <StoneXRow extraHorizontalSpacing>
                <StoneXDatePicker onChange={setQuoteDate} label="Quote Date" value={quoteDate} width='small' />
                <StoneXTextField label="Stress Increments" width="small" defaultValue={stressIncrementsText} onChange={onStressIncrementsChange} />
                <ToggleButtonGroup exclusive value={stressIncrementType} onChange={onStressIncrementTypeChange}>
                    <ToggleButton value={StressIncrementType.Percent}><Percent /></ToggleButton>
                    <ToggleButton value={StressIncrementType.Price}><AttachMoney /></ToggleButton>
                </ToggleButtonGroup>
                <ButtonGroup>
                    <StoneXButton variant="outlined" onClick={runReport} startIcon={<PlayArrow />} disabled={reportIsBusy}>Run</StoneXButton>
                    <StoneXButton variant="outlined" onClick={downloadToExcel} startIcon={<Download />} disabled={reportIsBusy}>Excel</StoneXButton>
                </ButtonGroup>

                <Button className='pull-right' variant='outlined' onClick={() => setIsConfiguringReport(true)}>Configure Report</Button>
            </StoneXRow>

            <br />

            <StoneXLoading show={reportIsBusy} />

            {reportLoadingState.isLoaded() && (
                <>
                    <StoneXRow align='center'>
                        <h2>{customerName} {customerReport.reportName}</h2>
                    </StoneXRow>
                    <StoneXRow align='center'>
                        <ToggleButtonGroup exclusive value={activeStressIncrement} onChange={onActiveStressIncrementChange} size='small'>
                            {stressOptions.map((option) => (
                                <ToggleButton key={option} value={option}>{formatStressLabel(option)}</ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </StoneXRow>
                    <ExposureReportTable
                        rows={rows}
                        columnsToShow={reportSettings?.columns?.active ?? []}
                        activeStressIncrement={activeStressIncrement}
                        stressIncrementType={stressIncrementType}
                    />
                </>
            )}

            <ExposureReportSettingsModal open={isConfiguringReport} customerReport={customerReport} onClose={closeModal} onSave={closeModal} />
        </div>
    );
}
